// Polyfill for `@international/mastodon-i18n`'s `i18n.getMomentLocale()`.
//
// Its final step loads moment's locale data for the resolved locale:
//
//   try { require(`moment/locale/${momentLocale}`); }
//   catch (e) { momentLocale = this.defaultLocale; }
//
// Webpack resolved that dynamic `require`; esbuild (Vite's dep optimizer)
// and rollup cannot statically resolve a `require` with a runtime variable
// and leave a stub that throws "Dynamic require of … is not supported", so
// the `catch` fires on every call and clobbers the correctly-resolved
// locale back to `en`. The visible effect was that every localized date
// *pattern* (`lib/dateFormat.ts` → `getLocalizedDateFormat`) came out
// English regardless of language across the international portal.
//
// This pairs with the `moment` → `moment-with-locales` alias in
// `vite.config.mts`: the alias gives the shared `moment` instance the
// locale *data* (month names, etc.), and this override returns the right
// locale *id* without the throwing `require`. Mirrors the approach in
// `i18nCountriesPolyfill`.
//
// Imported for side effects at the top of `src/index.tsx`, before
// `initializeTranslations()` — which calls `getMomentLocale()` to set the
// global moment locale, as does the header language toggle.

import { i18n } from '@international/mastodon-i18n';

const DEFAULT_LOCALE = i18n.defaultLocale; // 'en'

const patched = i18n as unknown as {
  getLocale: () => string | undefined;
  getMomentLocale: () => string;
};

// Map the active i18next locale to a moment locale id (e.g. `es_MX` ->
// `es-mx`, `es` -> `es`). moment falls back to the base language / `en`
// on its own for ids it doesn't recognize, so no allow-list is needed.
patched.getMomentLocale = (): string =>
  (patched.getLocale?.() || DEFAULT_LOCALE).toLowerCase().replace(/_/g, '-');

export {};
